// Shared cosmetic theme for challenge cards: a curated emoji grid + gradient swatches.
// The creator picks these at creation time; they are stored off-chain (Cloudflare KV).
// When a challenge has no saved theme (older challenges), we fall back to an index-based pick.

export const THEME_EMOJIS = [
  '🏃',
  '💪',
  '🏋️',
  '🧘',
  '🚴',
  '⛹️',
  '⏰',
  '🌅',
  '🥗',
  '🥦',
  '💧',
  '🍱',
  '🛏️',
  '📚',
  '📖',
  '✍️',
  '🧠',
  '💰',
  '🚭',
  '🧹',
  '🎨',
  '🎸',
  '🐢',
  '🔥',
] as const

export interface ThemeGradient {
  id: string
  css: string
}

export const THEME_GRADIENTS: ThemeGradient[] = [
  {
    id: 'indigo',
    css: 'linear-gradient(135deg, oklch(0.52 0.19 281) 0%, oklch(0.45 0.2 281) 100%)',
  },
  {
    id: 'teal',
    css: 'linear-gradient(135deg, oklch(0.56 0.12 168) 0%, oklch(0.48 0.13 168) 100%)',
  },
  { id: 'amber', css: 'linear-gradient(135deg, oklch(0.62 0.16 45) 0%, oklch(0.55 0.17 45) 100%)' },
  {
    id: 'blue',
    css: 'linear-gradient(135deg, oklch(0.54 0.16 256) 0%, oklch(0.47 0.17 256) 100%)',
  },
  { id: 'rose', css: 'linear-gradient(135deg, oklch(0.58 0.18 12) 0%, oklch(0.5 0.19 12) 100%)' },
  {
    id: 'green',
    css: 'linear-gradient(135deg, oklch(0.6 0.15 145) 0%, oklch(0.52 0.16 145) 100%)',
  },
  {
    id: 'violet',
    css: 'linear-gradient(135deg, oklch(0.55 0.2 310) 0%, oklch(0.47 0.2 310) 100%)',
  },
  { id: 'cyan', css: 'linear-gradient(135deg, oklch(0.6 0.13 220) 0%, oklch(0.52 0.14 220) 100%)' },
]

export const DEFAULT_COLOR_ID = THEME_GRADIENTS[0].id
export const DEFAULT_EMOJI = THEME_EMOJIS[0]

export function isValidEmoji(value: unknown): value is string {
  return typeof value === 'string' && (THEME_EMOJIS as readonly string[]).includes(value)
}

export function isValidColorId(value: unknown): value is string {
  return typeof value === 'string' && THEME_GRADIENTS.some((g) => g.id === value)
}

/** Resolve a stored color id to its gradient CSS; falls back to an index-based pick. */
export function resolveGradient(colorId: string | null | undefined, idx: number): string {
  const match = colorId ? THEME_GRADIENTS.find((g) => g.id === colorId) : undefined
  return (match ?? THEME_GRADIENTS[idx % THEME_GRADIENTS.length]).css
}

/** Resolve a stored emoji; falls back to an index-based pick. */
export function resolveEmoji(emoji: string | null | undefined, idx: number): string {
  return emoji && emoji.trim() ? emoji : THEME_EMOJIS[idx % THEME_EMOJIS.length]
}
